import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';

// MaterialUI
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

// app modules: components
import CollectionImage from 'components/CollectionImage';
import SearchField from 'components/SearchField';

// assets
import AddToCollectionStyles from './AddToCollection.scss';

const CollectionListItem = ({ loading, children, ...others }) => (
  <ListItem className="content-options__item" button {...others}>
    {children}
  </ListItem>
);

const CollectionItemProps = {
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
};
const CollectionItemDefaultProps = {
  loading: false,
};

CollectionListItem.propTypes = CollectionItemProps;
CollectionListItem.defaultProps = CollectionItemDefaultProps;

const RenderCollections = ({
  collections,
  action,
  extraProps = {},
  currentSortingOption,
  collectionFilter,
}) => {
  const sortOrderBy =
    currentSortingOption.key === 'available_from' ? 'updated_at' : currentSortingOption.key;
  const sortedCollections = collections
    .filter(collection =>
      collection.name
        ?.toLowerCase()
        .split(' ')
        .some(word => word.startsWith(collectionFilter?.toLowerCase())),
    )
    .sort((a, b) => {
      if (currentSortingOption.direction === 'asc') {
        return a[sortOrderBy]?.toLowerCase() > b[sortOrderBy]?.toLowerCase() ? 1 : -1;
      }
      return a[sortOrderBy]?.toLowerCase() < b[sortOrderBy]?.toLowerCase() ? 1 : -1;
    });

  return sortedCollections.map(collection => {
    let itemCount = '';
    if (collection.items_count && collection.items_count.total) {
      const { total } = collection.items_count;
      itemCount = `${total} item${total === 1 ? '' : 's'}`;
    }
    const image = (
      <ListItemIcon className="content-options__thumb content-options__thumb--collection">
        <CollectionImage
          mobileAsThumbnail
          contentImage={collection.cover_image}
          overrideImage={collection.custom_cover_image}
          width={50}
          height={50}
          bookCoverWidth={40}
          bookCoverHeight={40}
        />
      </ListItemIcon>
    );
    switch (collection.state) {
      case 'loading':
        return (
          <CollectionListItem loading key={`temporary-collection-${collection.id}`} {...extraProps}>
            {image}
            <ListItemText primary={collection.name} secondary={itemCount} />
          </CollectionListItem>
        );
      default:
        return (
          <CollectionListItem
            key={collection.id}
            onClick={() => {
              action(collection.id);
            }}
            {...extraProps}
          >
            {image}
            <ListItemText primary={collection.name} secondary={itemCount} />
          </CollectionListItem>
        );
    }
  });
};

function AddToCollection({
  SortingComponent,
  addItemToCollectionFn,
  collections,
  currentSortingOption,
  handleSortingOptionChange,
  handleOpenNewCollectionModal,
}) {
  useStyles(AddToCollectionStyles);

  const [collectionFilter, setCollectionFilter] = useState('');

  const extraProps = {};

  return (
    <div className="add-to-collection">
      <Button
        size="large"
        variant="contained"
        color="primary"
        onClick={handleOpenNewCollectionModal}
      >
        New Collection
      </Button>
      {collections.length > 0 && (
        <div className="add-to-collection__head-options">
          <SearchField
            autoComplete="off"
            fullWidth
            size="small"
            name="q"
            label="Find collection"
            placeholder="Find collection"
            value={collectionFilter}
            onChange={e => setCollectionFilter(e.target.value)}
            inputProps={{autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off'}}
          />
          {SortingComponent && (
            <SortingComponent
              currentSortingOption={currentSortingOption}
              onSortOrderChange={handleSortingOptionChange}
            />
          )}
        </div>
      )}
      {collections.length > 0 && (
        <div className="add-to-collection__list">
          <List disablePadding>
            <RenderCollections
              collections={collections}
              action={addItemToCollectionFn}
              extraProps={extraProps}
              currentSortingOption={currentSortingOption}
              collectionFilter={collectionFilter}
            />
          </List>
        </div>
      )}
    </div>
  );
}

AddToCollection.propTypes = {
  SortingComponent: PropTypes.func.isRequired,
  addItemToCollectionFn: PropTypes.func.isRequired,
  collections: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentSortingOption: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  handleSortingOptionChange: PropTypes.func.isRequired,
  handleOpenNewCollectionModal: PropTypes.func.isRequired,
};

export default AddToCollection;
